import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { MuiTelInput } from 'mui-tel-input';
import { useTelInputSettings } from '../../utils/phone';
import { Search, Car, User, Phone, Mail, MapPin } from 'lucide-react';
import { User as UserType } from '../../../../shared/types/user.types';
import { Garage } from '../../../../shared/types/garage.types';
import { Vehicle } from '../../../../shared/types/vehicle.types';
import useUser from '../../wrappers/use-user';
import useVehicle from '../../wrappers/use-vehicle';
import useGarage from '../../wrappers/use-garage';
import { useTranslation } from 'react-i18next';
import { serviceDeskStyles, vinInputStyles } from './ServiceDesk.styles';

type CustomerWithGarage = UserType & { garage?: Garage };

const ServiceDesk: React.FC = () => {
    const { t } = useTranslation();
    const telInputSettings = useTelInputSettings();
    const [activeTab, setActiveTab] = useState(0);

    // VIN search
    const [vin, setVin] = useState('');
    const [vinSearching, setVinSearching] = useState(false);
    const [vinError, setVinError] = useState<string | null>(null);
    const [vinResults, setVinResults] = useState<Vehicle[] | null>(null);

    // Customer search
    const [customerSearch, setCustomerSearch] = useState({ name: '', phone: '', email: '', address: '' });
    const [customerSearching, setCustomerSearching] = useState(false);
    const [customerResults, setCustomerResults] = useState<CustomerWithGarage[] | null>(null);

    const navigate = useNavigate();
    const users = useUser();
    const { searchByVinPrefix } = useVehicle();
    const { get: getGarage } = useGarage();

    const handleVinSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!vin.trim()) return;

        setVinSearching(true);
        setVinError(null);
        setVinResults(null);

        try {
            const results = await searchByVinPrefix(vin.trim());
            if (results.length === 0) {
                setVinError(t('serviceDesk.vinNotFound', { vin: vin.trim().toUpperCase() }));
            } else if (results.length === 1) {
                navigate(`/app/service-desk/vehicles/${results[0].vin}`);
            } else {
                setVinResults(results);
            }
        } catch {
            setVinError(t('serviceDesk.vinError'));
        } finally {
            setVinSearching(false);
        }
    };

    const handleCustomerSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        const { name, phone, email, address } = customerSearch;
        setCustomerSearching(true);
        setCustomerResults(null);
        try {
            const all = await users.list({ role: 'customer' });
            const filtered = all.users.filter((u) => {
                const nameLower = u.name.toLowerCase();
                const firstLast = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();

                if (name.trim() && !nameLower.includes(name.toLowerCase()) && !firstLast.includes(name.toLowerCase())) return false;
                if (phone.trim() && !u.phone?.includes(phone.trim())) return false;
                if (email.trim() && !u.email.toLowerCase().includes(email.toLowerCase())) return false;

                return true;
            });

            const withGarage: CustomerWithGarage[] = await Promise.all(
                filtered.map(async (u) => {
                    if (!u.garageUid) return u;
                    const garage = await getGarage(u.garageUid);
                    return { ...u, garage: garage ?? undefined };
                })
            );

            const addressLower = address.trim().toLowerCase();
            const final = addressLower
                ? withGarage.filter((u) => {
                    const a = u.garage?.address;
                    if (!a) return false;
                    const full = `${a.street} ${a.houseNumber}, ${a.zip} ${a.city}`.toLowerCase();
                    return full.includes(addressLower);
                })
                : withGarage;

            setCustomerResults(final);
        } catch (err) {
            console.error(err)
            setCustomerResults([]);
        } finally {
            setCustomerSearching(false);
        }
    };

    const handleCustomerFieldChange = (field: keyof typeof customerSearch, value: string) => {
        setCustomerSearch((prev) => ({ ...prev, [field]: value }));
        setCustomerResults(null);
    };

    const hasCustomerInput = Object.values(customerSearch).some((v) => v.trim());

    return (
        <Box sx={serviceDeskStyles.root}>
            <Box sx={serviceDeskStyles.titleSection}>
                <Typography variant="h4" fontWeight={700}>{t('serviceDesk.title')}</Typography>
                <Typography color="text.secondary" mt={1}>{t('serviceDesk.subtitle')}</Typography>
            </Box>

            <Paper elevation={0} sx={serviceDeskStyles.containerPaper}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={serviceDeskStyles.tabs}
                    variant="fullWidth"
                >
                    <Tab
                        icon={<Car size={24} />}
                        iconPosition="start"
                        label={t('serviceDesk.vinTab')}
                        sx={serviceDeskStyles.tab}
                    />
                    <Tab
                        icon={<User size={24} />}
                        iconPosition="start"
                        label={t('serviceDesk.customerTab')}
                        sx={serviceDeskStyles.tab}
                    />
                </Tabs>

                <Box sx={serviceDeskStyles.content}>
                    {activeTab === 0 && (
                        <Box sx={serviceDeskStyles.sectionColumn}>
                            <Box component="form" onSubmit={handleVinSearch} sx={serviceDeskStyles.form}>
                                <TextField
                                    label={t('serviceDesk.vinLabel')}
                                    fullWidth
                                    placeholder={t('serviceDesk.vinPlaceholder')}
                                    value={vin}
                                    onChange={(e) => { setVin(e.target.value); setVinError(null); setVinResults(null); }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={24} /></InputAdornment> }}
                                    inputProps={{ style: vinInputStyles }}
                                    error={!!vinError}
                                    disabled={vinSearching}
                                />
                                {vinError && <Alert severity="error">{vinError}</Alert>}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    disabled={vinSearching || !vin.trim()}
                                    startIcon={vinSearching ? <CircularProgress size={20} color="inherit" /> : undefined}
                                >
                                    {vinSearching ? t('serviceDesk.vinSearching') : t('serviceDesk.vinSearch')}
                                </Button>
                            </Box>
                            {vinResults !== null && (
                                <Box>
                                    <Divider sx={serviceDeskStyles.resultsDivider} />
                                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                                        {t('serviceDesk.foundMachines', { count: vinResults.length })}
                                    </Typography>
                                    <Paper variant="outlined" sx={serviceDeskStyles.resultsListPaper}>
                                        {vinResults.map((vehicle, i) => (
                                            <Box key={vehicle.uid}>
                                                {i > 0 && <Divider />}
                                                <Box
                                                    onClick={() => navigate(`/app/service-desk/vehicles/${vehicle.vin}`)}
                                                    sx={serviceDeskStyles.resultRow}
                                                >
                                                    <Box sx={serviceDeskStyles.resultRowMain}>
                                                        <Box sx={serviceDeskStyles.vehicleIconBox}>
                                                            <Car size={18} color="#009fe3" />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>{vehicle.model}</Typography>
                                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">{vehicle.vin}</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="caption" color="primary.main" fontWeight={500}>
                                                        {t('serviceDesk.viewDetail')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box sx={serviceDeskStyles.sectionColumn}>
                            <Box component="form" onSubmit={handleCustomerSearch} sx={serviceDeskStyles.form}>
                                <TextField
                                    label={t('serviceDesk.nameLabel')}
                                    fullWidth
                                    placeholder={t('serviceDesk.namePlaceholder')}
                                    value={customerSearch.name}
                                    onChange={(e) => handleCustomerFieldChange('name', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><User size={24} /></InputAdornment> }}
                                    disabled={customerSearching}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <MuiTelInput
                                            label={t('common.phone')}
                                            fullWidth
                                            value={customerSearch.phone}
                                            onChange={(value) => handleCustomerFieldChange('phone', value)}
                                            {...telInputSettings}
                                            disabled={customerSearching}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label={t('common.email')}
                                            fullWidth
                                            placeholder={t('serviceDesk.emailPlaceholder')}
                                            value={customerSearch.email}
                                            onChange={(e) => handleCustomerFieldChange('email', e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={24} /></InputAdornment> }}
                                            disabled={customerSearching}
                                        />
                                    </Grid>
                                </Grid>
                                <TextField
                                    label={t('serviceDesk.garageAddressLabel')}
                                    fullWidth
                                    placeholder={t('serviceDesk.garageAddressPlaceholder')}
                                    value={customerSearch.address}
                                    onChange={(e) => handleCustomerFieldChange('address', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={24} /></InputAdornment> }}
                                    disabled={customerSearching}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    disabled={customerSearching || !hasCustomerInput}
                                    startIcon={customerSearching ? <CircularProgress size={20} color="inherit" /> : undefined}
                                >
                                    {customerSearching ? t('serviceDesk.customerSearching') : t('serviceDesk.customerSearch')}
                                </Button>
                            </Box>

                            {customerResults !== null && (
                                <Box>
                                    <Divider sx={serviceDeskStyles.resultsDivider} />
                                    {customerResults.length === 0 ? (
                                        <Alert severity="info">{t('serviceDesk.noCustomers')}</Alert>
                                    ) : (
                                        <Box sx={serviceDeskStyles.customerResultsWrapper}>
                                            <Typography variant="body2" color="text.secondary" mb={1.5}>
                                                {t('serviceDesk.foundCustomers', { count: customerResults.length })}
                                            </Typography>
                                            <Paper variant="outlined" sx={serviceDeskStyles.resultsListPaper}>
                                                {customerResults.map((customer, i) => (
                                                    <Box key={customer.uid}>
                                                        {i > 0 && <Divider />}
                                                        <Box
                                                            onClick={() => navigate(`/app/service-desk/customers/${customer.uid}`)}
                                                            sx={serviceDeskStyles.resultRow}
                                                        >
                                                            <Box sx={serviceDeskStyles.resultRowMain}>
                                                                <Box sx={serviceDeskStyles.customerIconBox}>
                                                                    <User size={18} color="#009fe3" />
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={600}>{customer.name}</Typography>
                                                                    <Box sx={serviceDeskStyles.customerMeta}>
                                                                        <Typography variant="caption" color="text.secondary" sx={serviceDeskStyles.customerMetaItem}>
                                                                            <Mail size={12} />{customer.email}
                                                                        </Typography>
                                                                        {customer.phone && (
                                                                            <Typography variant="caption" color="text.secondary" sx={serviceDeskStyles.customerMetaItem}>
                                                                                <Phone size={12} />{customer.phone}
                                                                            </Typography>
                                                                        )}
                                                                        {customer.garage?.address && (
                                                                            <Typography variant="caption" color="text.secondary" sx={serviceDeskStyles.customerMetaItem}>
                                                                                <MapPin size={12} />
                                                                                {customer.garage.address.street} {customer.garage.address.houseNumber}, {customer.garage.address.zip} {customer.garage.address.city}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                            <Typography variant="caption" color="primary.main" fontWeight={500}>
                                                                {t('serviceDesk.viewDetail')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Paper>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ServiceDesk;
